import React, { useState, useEffect, memo, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api';
import ModalAssinatura from './ModalAssinatura';
import useDialogoTeclado from '../hooks/useDialogoTeclado';
import Icon from './ui/Icon';
import {
  NAV_SECTIONS,
  filterNavForRole,
  isNavItemActive,
  sectionContainsPath,
} from '../navigation';
import SeletorFazenda from './SeletorFazenda';
import { ouvirFazenda } from '../sessao';
import useSouMaster from '../hooks/useSouMaster';

function varianteAssinatura(a) {
  if (a.status === 'VENCIDO') return 'perigo';
  if (a.status === 'TRIAL') return 'info';
  if (a.diasRestantes <= 5) return 'alerta';
  return 'ok';
}

function iconeAssinatura(status) {
  if (status === 'VENCIDO') return 'alerta';
  if (status === 'TRIAL') return 'presente';
  return 'ok';
}

const MenuLink = memo(({ to, label, icon, badge, isActive, onClick, nested }) => (
  <Link
    to={to}
    className={`menu-link ${nested ? 'menu-link--nested' : ''} ${isActive ? 'active' : ''}`}
    onClick={onClick}
  >
    <span className="menu-link-icon"><Icon nome={icon} tamanho={19} /></span>
    <span className="menu-link-text">{label}</span>
    {badge > 0 && (
      <span className="menu-link-badge">
        {badge}
      </span>
    )}
  </Link>
));

function MenuSection({ section, pathname, search, onClose, defaultOpen, pendentes = 0 }) {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen, pathname]);

  // Notificações fica dentro de "Conta"; com a seção fechada o badge sobe para
  // o cabeçalho para o aviso não ficar escondido.
  const temNotificacoes = section.items.some((i) => i.to === '/notificacoes');

  return (
    <div className="menu-group">
      <button
        type="button"
        className={`menu-group-toggle ${open ? 'menu-group-toggle--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="menu-group-label">
          {section.label}
          {temNotificacoes && !open && pendentes > 0 && (
            <span className="menu-link-badge">{pendentes}</span>
          )}
        </span>
        <span className="menu-group-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="menu-group-items">
          {section.items.map((item) => (
            <MenuLink
              key={item.to}
              to={item.to}
              label={item.label}
              icon={item.icon}
              badge={item.to === '/notificacoes' ? pendentes : 0}
              isActive={isNavItemActive(item, pathname, search)}
              onClick={onClose}
              nested
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Menu({ open, onClose, onLogout, pendentes = 0 }) {
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem('role') === 'ADMIN');
  const [codigoConvite, setCodigoConvite] = useState(localStorage.getItem('codigoConvite'));
  const [tooltipVisivel, setTooltipVisivel] = useState(false);
  const [assinatura, setAssinatura] = useState(null);
  const [modalPagamento, setModalPagamento] = useState(false);

  // Papel e código de convite seguem a fazenda ativa.
  useEffect(() => ouvirFazenda(() => {
    setIsAdmin(localStorage.getItem('role') === 'ADMIN');
    setCodigoConvite(localStorage.getItem('codigoConvite'));
  }), []);

  const painelRef = useDialogoTeclado(open, onClose);
  const isMaster = useSouMaster();

  const sections = useMemo(
    () => filterNavForRole(NAV_SECTIONS, isAdmin, isMaster),
    [isAdmin, isMaster]
  );

  useEffect(() => {
    if (!open) return;
    api.getAssinatura().then(data => {
      setAssinatura(data);
      sessionStorage.setItem('assinatura_cache', JSON.stringify({ data, ts: Date.now() }));
    }).catch(() => {});
  }, [open]);

  const statusConhecido = ['TRIAL', 'ATIVO', 'VENCIDO'].includes(assinatura?.status);

  if (!open) return null;

  return (
    <div className="menu-overlay" onClick={onClose} role="presentation">
      <div
        className="menu-panel"
        id="menu-principal"
        ref={painelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu principal"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        <div className="menu-header">
          <button type="button" className="menu-close" onClick={onClose} aria-label="Fechar menu">
            <Icon nome="fechar" tamanho={20} />
          </button>
          <div className="menu-brand">
            <span className="marca-icone marca-icone--lg menu-brand-icon" aria-hidden="true">
              <img src={process.env.PUBLIC_URL + '/icone-sem-fundo.png'} alt="" />
            </span>
            <div className="menu-brand-title">AgriGest</div>
            {localStorage.getItem('usuario') && <div className="menu-brand-user">{localStorage.getItem('usuario')}</div>}
            <SeletorFazenda variante="menu" />
            {codigoConvite && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', justifyContent: 'center' }}>
                  <span>Código de acesso:</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--accent)', letterSpacing: 1 }}>
                    {codigoConvite}
                  </span>
                  <button
                    onClick={() => setTooltipVisivel(v => !v)}
                    aria-label="O que é o código de acesso?"
                    aria-expanded={tooltipVisivel}
                    style={{
                      width: 16, height: 16, borderRadius: '50%', border: '1.5px solid var(--text-muted)',
                      background: tooltipVisivel ? 'var(--text-muted)' : 'transparent',
                      color: tooltipVisivel ? 'white' : 'var(--text-muted)',
                      fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: 0, minHeight: 'unset', boxShadow: 'none', flexShrink: 0, lineHeight: 1
                    }}
                  ><span aria-hidden="true">?</span></button>
                </div>
                {tooltipVisivel && (
                  <div style={{
                    marginTop: '0.4rem', background: '#f0f2f5', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)', padding: '0.5rem 0.65rem',
                    fontSize: '0.72rem', color: 'var(--text)', lineHeight: 1.5, textAlign: 'left'
                  }}>
                    Compartilhe este código com quem você quer convidar para acessar sua fazenda.
                    A pessoa usa o código na tela de cadastro para solicitar acesso.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {statusConhecido && (
          <button
            type="button"
            onClick={() => setModalPagamento(true)}
            className={`menu-assinatura menu-assinatura--${varianteAssinatura(assinatura)}`}
          >
            <Icon nome={iconeAssinatura(assinatura.status)} tamanho={16} />
            <div style={{ flex: 1 }}>
              <div className="menu-assinatura__titulo">
                {assinatura.status === 'TRIAL' && `Período gratuito · ${assinatura.diasRestantes} dia(s)`}
                {assinatura.status === 'ATIVO' && `Plano ${assinatura.plano} · R$ ${assinatura.valorMensal?.toFixed(2).replace('.', ',')}/mês`}
                {assinatura.status === 'VENCIDO' && 'Assinatura vencida'}
              </div>
              <div className="menu-assinatura__detalhe">
                {assinatura.status === 'VENCIDO' ? 'Clique aqui para pagar' : 'Clique para ver detalhes'}
              </div>
            </div>
          </button>
        )}

        {modalPagamento && assinatura && (
          <ModalAssinatura assinatura={assinatura} onClose={() => setModalPagamento(false)} />
        )}

        <nav className="menu-nav">
          <MenuLink
            to="/"
            label="Início"
            icon="inicio"
            isActive={location.pathname === '/'}
            onClick={onClose}
          />

          {sections.map((section) => (
            <MenuSection
              key={section.id}
              section={section}
              pathname={location.pathname}
              search={location.search}
              onClose={onClose}
              defaultOpen={sectionContainsPath(section, location.pathname)}
              pendentes={pendentes}
            />
          ))}

          <div className="menu-divider" />
          <a
            href="https://wa.me/5554999487383?text=Ol%C3%A1!%20Gostaria%20de%20falar%20sobre%20o%20AgriGest"
            target="_blank"
            rel="noopener noreferrer"
            className="menu-link"
            onClick={onClose}
          >
            <span className="menu-link-icon" aria-hidden="true"><svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg></span>
            <span className="menu-link-text">Fale Conosco</span>
          </a>
          <button className="menu-link menu-logout" onClick={() => { onClose(); onLogout(); }}>
            <span className="menu-link-icon"><Icon nome="sair" tamanho={19} /></span>
            <span className="menu-link-text">Sair</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
